/**
 * "Where are you?" — one-tap region pick for customers without an assignment.
 * Region routes the customer to the right branch team (master plan §2.1).
 */
import { useEffect, useState } from "react";
import { Loader2, MapPin } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { platformApi } from "@/platform/api";
import { usePlatformAuth } from "@/platform/PlatformAuthContext";

export function RegionPrompt() {
  const { user, refreshUser } = usePlatformAuth();
  const { toast } = useToast();
  const [regions, setRegions] = useState<{ id: string; name: string }[]>([]);
  const [saving, setSaving] = useState<string | null>(null);

  const needsRegion = Boolean(user && user.role === "customer" && !user.region_id);

  useEffect(() => {
    if (!needsRegion) return;
    platformApi.regions()
      .then((data) => setRegions(data.regions))
      .catch(() => setRegions([]));
  }, [needsRegion]);

  if (!needsRegion || regions.length === 0) return null;

  const choose = async (regionId: string) => {
    setSaving(regionId);
    try {
      await platformApi.updateProfile({ region_id: regionId });
      await refreshUser();
      toast({ title: "Thanks!", description: "Your local team will handle your account." });
    } catch (error) {
      toast({
        title: "Could not save",
        description: error instanceof Error ? error.message : "Try again shortly.",
        variant: "destructive",
      });
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open>
      <DialogContent className="max-w-sm" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" /> Where are you?
          </DialogTitle>
          <DialogDescription>
            Pick your nearest town so the right local team supports you.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          {regions.map((region) => (
            <Button
              key={region.id}
              variant="outline"
              className="justify-start"
              disabled={saving !== null}
              onClick={() => choose(region.id)}
            >
              {saving === region.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {region.name}
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
